import React from 'react';
import { computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react';
import { alpha, Box, Chip, Stack, styled, Tooltip, Typography } from '@mui/material';
import CountUp from 'react-countup';
import Chart from 'react-apexcharts';

import ErrorBoundary from '../../../utils/ErrorBoundary';
import { SingleValueStatModel } from '../../../stores/insight/SingleValueStatModel';
import { NumberUtil } from '../../../helpers/NumberUtil';

const StyledBox = styled(Box)(({ theme }) => ({
	position: 'relative',
	borderRadius: theme.shape.borderRadius,
	padding: theme.spacing(2),
	border: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
	backgroundColor: alpha(theme.palette.background.paper, 0.2),
	overflow: 'hidden',
	flexGrow: 1,
	display: 'flex',
	height: '100%',
}));

type Props = {
	number: number;
	unit?: string;
	title: string;
	textColor?: 'primary' | 'inherit' | 'initial' | 'secondary' | 'textPrimary' | 'textSecondary' | 'error';
	chartType?:
		| 'area'
		| 'line'
		| 'bar'
		| 'pie'
		| 'donut'
		| 'radialBar'
		| 'scatter'
		| 'bubble'
		| 'heatmap'
		| 'treemap'
		| 'boxPlot'
		| 'candlestick'
		| 'radar'
		| 'polarArea'
		| 'rangeBar';
	series?: { name: string; data: number[] }[];
	comparison?: SingleValueStatModel;
};

const SingleValueStat = observer(
	class SingleValueStat extends React.Component<Props> {
		_number: number = 0;

		options: any = {
			chart: {
				id: 'basic-bar',
				toolbar: {
					show: false,
				},
			},
			colors: ['rgba(255,255,255,0.5)', 'rgba(255,255,255,0.4)'],
			animations: {
				enabled: true,
				easing: 'easeinout',
				speed: 800,
				animateGradually: {
					enabled: true,
					delay: 150,
				},
				dynamicAnimation: {
					enabled: true,
					speed: 350,
				},
			},
			markers: {
				size: 0,
				show: false,
			},
			dataLabels: {
				enabled: false,
			},
			xaxis: {
				type: 'number',
				// categories: [1991, 1992, 1993, 1994, 1995, 1996, 1997, 1998, 1999],
				labels: {
					show: false,
				},
				axisTicks: {
					show: false,
				},
				axisBorder: {
					show: false,
				},
			},
			yaxis: {
				labels: {
					show: false,
				},
				axisTicks: {
					show: false,
				},
			},
			legend: {
				show: false,
			},
			grid: {
				show: false,
			},
		};

		chartType:
			| 'area'
			| 'line'
			| 'bar'
			| 'histogram'
			| 'pie'
			| 'donut'
			| 'radialBar'
			| 'scatter'
			| 'bubble'
			| 'heatmap'
			| 'treemap'
			| 'boxPlot'
			| 'candlestick'
			| 'radar'
			| 'polarArea'
			| 'rangeBar' = 'area';

		constructor(props: Props) {
			super(props);

			makeObservable(this, {
				number: computed,
				unit: computed,
				title: computed,
				growth: computed,
				growthPercentage: computed,
			});
		}

		get number() {
			return this.props.number ?? 0;
		}

		get unit() {
			return this.props.unit ? this.props.unit : '';
		}

		get title() {
			return this.props.title;
		}

		get growth() {
			if (!this.props.comparison) {
				return undefined;
			}

			// calc growth basedd on number
			return this.number - this.props.comparison.number;
		}

		get growthPercentage() {
			if (this.growth === undefined) {
				return 0;
			}

			if (this.props.comparison?.number === 0 || this.props.comparison?.number === undefined) {
				return 0;
			}

			return (this.growth / this.props.comparison.number) * 100;
		}

		get series() {
			return this.props.series ?? [];
		}

		get textColor():
			| 'primary'
			| 'inherit'
			| 'initial'
			| 'secondary'
			| 'textPrimary'
			| 'textSecondary'
			| 'error'
			| undefined {
			return this.props.textColor ?? 'textSecondary';
		}

		render() {
			const { chartType, comparison } = this.props;
			const { number, denominator } = NumberUtil.numberToShortString(this.number);
			const comparisonIsLarger = (comparison?.number ?? 0) > this.number;

			return (
				<StyledBox className="SingleValueStat">
					<Stack direction="column" spacing={1} alignItems="space-between" sx={{ width: '100%' }}>
						<Typography variant="body2" color="inherit" className="title">
							{this.title}
						</Typography>
						<Box sx={{ flexGrow: 1 }} />
						<Stack
							direction="row"
							justifyContent="space-between"
							sx={{ width: '100%' }}
							alignItems="center"
						>
							<Tooltip title={`${this.number} ${this.unit}`} placement="top">
								<Typography variant="h6" color={this.textColor} sx={{ fontWeight: 'bold' }}>
									<CountUp
										decimals={2}
										end={number}
										duration={2.75}
										suffix={`${denominator} ${this.unit}`}
									/>
								</Typography>
							</Tooltip>

							{this.growthPercentage !== 0 && comparison && (
								<Chip
									sx={{
										backgroundColor: ({ palette }) => {
											if (comparisonIsLarger) {
												return alpha(palette.error.main, 0.1);
											}
											return alpha(palette.success.main, 0.1);
										},
										color: ({ palette }) => {
											if (comparisonIsLarger) {
												return alpha(palette.error.main, 1);
											}
											return alpha(palette.success.dark, 1);
										},
										borderColor: ({ palette }) => {
											if (comparisonIsLarger) {
												return palette.error.main;
											}
											return palette.success.main;
										},
									}}
									label={
										<>
											{comparisonIsLarger ? '↓' : '↑'}{' '}
											<CountUp end={this.growthPercentage ?? 0} duration={2.75} suffix="%" />
										</>
									}
									size="small"
								/>
							)}
						</Stack>
					</Stack>
					{chartType && this.series && (
						<Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1 }}>
							<ErrorBoundary>
								<Chart
									options={this.options}
									series={this.series}
									type={chartType}
									width="100%"
									height="150"
									style={{ width: '100%', height: '100%' }}
								/>
							</ErrorBoundary>
						</Box>
					)}
				</StyledBox>
			);
		}
	}
);

export default SingleValueStat;
